import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { IntakeFormDefinitionSchema } from '@aline/common';
import { Autocomplete } from '@mui/material';
import { useSnackbar } from 'notistack';
import { AlineAutocompleteInput, AlineButton } from 'src/components';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { useIntakeFormTemplate } from './use-intake-form-template';

type IntakeTemplateOption = ApolloTypes.IntakeFormTemplateFullFragment;

const useStyles = makeStyles()(theme => ({
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(3),
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionMeta: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  optionTitle: {
    color: theme.appColors.iconsType,
    fontWeight: 600,
  },
}));

function getFieldCount(template: IntakeTemplateOption): number {
  const parsed = IntakeFormDefinitionSchema.safeParse(template.formDefinition);
  return parsed.success ? parsed.data.fields.length : 0;
}

interface LinkIntakeFormDialogProps {
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}

export function LinkIntakeFormDialog({ onClose, open, workflowId }: LinkIntakeFormDialogProps) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [linking, setLinking] = useState(false);
  const { linkIntakeForm } = useIntakeFormTemplate(workflowId);
  const { data } = Apollo.useIntakeFormTemplatesQuery({ skip: !open });
  const [selected, setSelected] = useState<IntakeTemplateOption | null>(null);
  const templates = data?.intakeFormTemplates ?? [];

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleLink = async () => {
    if (!selected) return;

    try {
      setLinking(true);
      await linkIntakeForm(selected.id);
      handleClose();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to link intake form.', {
        variant: 'error',
      });
    } finally {
      setLinking(false);
    }
  };

  return (
    <AlineDialog
      fullWidth
      maxWidth="sm"
      open={open}
      title="Link an intake form"
      onClose={handleClose}
    >
      <Autocomplete<IntakeTemplateOption>
        autoHighlight
        openOnFocus
        getOptionLabel={option => option.title || 'Untitled form'}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        options={templates}
        renderInput={params => (
          <AlineAutocompleteInput {...params} placeholder="Search intake forms by title…" />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const count = getFieldCount(option);
          return (
            <li key={key} {...optionProps}>
              <div className={classes.option}>
                <span className={classes.optionTitle}>{option.title || 'Untitled form'}</span>
                <span className={classes.optionMeta}>
                  {count} field{count === 1 ? '' : 's'}
                </span>
              </div>
            </li>
          );
        }}
        value={selected}
        onChange={(_event, option) => setSelected(option)}
      />
      <div className={classes.footer}>
        <AlineButton color="primary" size="small" variant="outlined" onClick={handleClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!selected || linking}
          size="small"
          variant="contained"
          onClick={handleLink}
        >
          Link
        </AlineButton>
      </div>
    </AlineDialog>
  );
}
